<script lang="ts" setup>
import { openChatWindow, openGlobalUserProfile } from '@/components/helper'
import { FollowState } from '@/composition/follow'
import { GroupState } from '@/composition/groups'
import { Theme } from '@/composition/themes'
import { UserState } from '@/composition/users'
import { handleFollowHelper } from '@/helper/follow.helper'
import { useToastNotification } from '@/helper/notification.toast.helper'
import { GroupMemberRole } from '@/models/GroupMember'
import { IUser } from '@/models/Users'
import { GAMIFICATION_LEVELS } from '@/util/const'
import { getLastActivityDate } from '@/util/helper'
import {
  FacebookDefaultIcon,
  InstragramDefaultIcon,
  LinkedinDefaultIcon,
  TwitterIcon,
  YoutubeDefaultIcon,
} from '@gohighlevel/ghl-icons/24/misc'
import { ClockIcon, MarkerPin02Icon } from '@gohighlevel/ghl-icons/24/outline'
import {
  UITextLgMedium,
  UITextMdMedium,
  UITextMdNormal,
  UITextSmMedium,
  UITextSmNormal,
  UITextXsMedium,
  UITextXsRegular,
  UITooltip,
} from '@gohighlevel/ghl-ui'
import { NProgress } from 'naive-ui'
import { PropType, computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import BadgedAvatar from '../avatar/BadgedAvatar.vue'
import CommunityButton from '../buttons/CommunityButton.vue'

const toastNotification = useToastNotification()

const props = defineProps({
  user: {
    type: Object as PropType<IUser>,
    required: false,
  },
  userRole: {
    type: String as PropType<GroupMemberRole>,
    required: false,
  },
  showBadge: {
    type: Boolean,
    required: false,
    default: true,
  },
})

const router = useRouter()

const { t } = useI18n()

// Add reactive reference for follow status
const isFollowing = ref(false)

const levelCompletionPercentage = computed(() => {
  const currentPoints = props.user?.gamificationMeta?.points ?? 0
  const currentLevel = props.user?.gamificationMeta?.level ?? 1
  const nextLevelMinPoints =
    currentLevel === 9
      ? GAMIFICATION_LEVELS[currentLevel].maxPoints
      : GAMIFICATION_LEVELS[currentLevel + 1].minPoints
  const currentLevelMinPoints = GAMIFICATION_LEVELS[currentLevel].minPoints
  const previousLevelMaxPoints =
    currentLevel === 1 ? 0 : GAMIFICATION_LEVELS[currentLevel - 1].maxPoints
  const totalPointsRangeInCurrentLevel =
    nextLevelMinPoints - currentLevelMinPoints
  const pointsInCurrentLevel = currentPoints - previousLevelMaxPoints

  return (pointsInCurrentLevel / totalPointsRangeInCurrentLevel) * 100
})
const userLevel = computed(() => {
  const level = props.user?.gamificationMeta?.level ?? 1
  if (GroupState.state.groupData) {
    let levelName = t('leaderboard.level') + ' ' + level
    if (
      GroupState.state.groupData.settings?.gamificationSettings?.levelNames?.[
        level.toString()
      ]
    ) {
      levelName =
        levelName +
        ' - ' +
        GroupState.state.groupData.settings?.gamificationSettings?.levelNames[
          level.toString()
        ]
    }
    return levelName
  } else {
    return undefined
  }
})

// Remove the async computed property and replace with this:
// Check follow status when component mounts or user changes
onMounted(() => {
  checkFollowStatus()
})

watch(
  () => props.user?.contactId,
  () => {
    checkFollowStatus()
  }
)

async function checkFollowStatus() {
  if (props.user?.contactId) {
    isFollowing.value = await FollowState.isFollowing(props.user.contactId)
  }
}

function openLink(link?: string) {
  if (!link) return
  window.open(link, '_blank')
}

// const lastLoginRelativeTIm
const sanitizedFullName = computed(() => {
  return props.user?.fullName || t('userProfilePopover.deletedUser')
})

async function handleFollowUser() {
  const response = await handleFollowHelper(
    props.user?.contactId ?? '',
    isFollowing.value
  )
  if (response.success) {
    isFollowing.value = !isFollowing.value
  } else if (response.message) {
    toastNotification.showError(t(response.message))
  }
}
</script>
<template>
  <div
    class="max-w-[460px] overflow-hidden rounded-xl bg-communities-background p-6"
  >
    <div class="profile-popover-layout grid">
      <div class="flex flex-col items-center justify-center pb-2">
        <NProgress
          :percentage="levelCompletionPercentage + 1"
          type="circle"
          :offset-degree="180"
          :stroke-width="3"
          :color="
            Theme.state.isDarkMode
              ? ' var(--primaryLight)'
              : 'var(--primaryDark)'
          "
        >
          <template #default>
            <div>
              <BadgedAvatar
                :avatar-url="user?.avatar"
                :full-name="user?.fullName"
                :show-badge="showBadge"
                :role="userRole"
                :size="152"
                :level="user?.gamificationMeta?.level?.toString()"
              />
            </div>
          </template>
        </NProgress>
        <UITextSmMedium class="mt-1.5 text-communities-font-primary">
          {{ userLevel }}
        </UITextSmMedium>
        <div class="flex flex-row">
          <UITextXsMedium class="text-communities-font-primary"
            >{{
              GAMIFICATION_LEVELS[(user?.gamificationMeta?.level ?? 1) + 1]
                .minPoints - (user?.gamificationMeta?.points ?? 0)
            }}
            &nbsp;</UITextXsMedium
          >
          <UITextXsRegular class="text-communities-font-secondary">
            {{ t('userProfilePopover.pointsToLevelUp') }}
          </UITextXsRegular>
        </div>
      </div>
      <div class="flex flex-col justify-start overflow-hidden">
        <UITextLgMedium
          v-sanitized-html="sanitizedFullName"
          class="line-clamp-1 text-xl font-semibold text-communities-font-primary"
        />

        <div class="py-1.5">
          <div class="grid-auto-col-2 grid items-center">
            <ClockIcon class="h-3.5 w-3.5 text-communities-font-secondary" />
            <UITextSmNormal class="truncate text-communities-font-secondary">
              {{ t('userProfilePopover.active') }}
              {{
                getLastActivityDate([
                  user?.joiningDate,
                  user?.lastActivityAt,
                  user?.lastVisitedAt,
                ])
              }}
            </UITextSmNormal>
          </div>
          <div
            v-if="user?.location"
            class="grid-auto-col-2 grid items-center pt-1"
          >
            <MarkerPin02Icon
              class="h-3.5 w-3.5 text-communities-font-secondary"
            />
            <UITextSmNormal class="truncate text-communities-font-secondary">
              {{ user?.location }}
            </UITextSmNormal>
          </div>
        </div>
        <UITextMdNormal
          v-if="user?.title"
          class="line-clamp-1 text-communities-font-primary"
        >
          {{ user?.title }}
        </UITextMdNormal>
        <UITextMdNormal
          v-if="user?.bio"
          class="line-clamp-2 pt-2 text-left text-communities-font-secondary"
        >
          {{ user?.bio }}
        </UITextMdNormal>
        <div
          v-if="user?.socials"
          class="mt-2.5 flex flex-row gap-4 text-communities-font-primary"
        >
          <TwitterIcon
            v-if="user?.socials?.twitter"
            class="social-icons h-5 w-5 cursor-pointer"
            @click="() => openLink(user?.socials?.twitter)"
          />
          <LinkedinDefaultIcon
            v-if="user?.socials?.linkedin"
            class="social-icons h-5 w-5 cursor-pointer"
            @click="() => openLink(user?.socials?.linkedin)"
          />
          <YoutubeDefaultIcon
            v-if="user?.socials?.youtube"
            class="social-icons h-5 w-5 cursor-pointer"
            @click="() => openLink(user?.socials?.youtube)"
          />
          <FacebookDefaultIcon
            v-if="user?.socials?.facebook"
            class="social-icons h-5 w-5 cursor-pointer"
            @click="() => openLink(user?.socials?.facebook)"
          />
          <InstragramDefaultIcon
            v-if="user?.socials?.instagram"
            class="instagram-icon h-5 w-5 cursor-pointer"
            @click="() => openLink(user?.socials?.instagram)"
          />
        </div>
      </div>
    </div>
    <div
      class="grid gap-4"
      :class="
        user?.contactId != UserState.state.currentUser?.contactId
          ? 'grid-cols-3'
          : 'grid-cols-1'
      "
    >
      <CommunityButton
        id="view-profile"
        class="mt-4 h-12 w-full uppercase"
        button-type="tertiary"
        size="small"
        @click="openGlobalUserProfile(user?.slug as string, router)"
        ><UITextMdMedium class="font-semibold">
          {{ t('userProfilePopover.profile') }}
        </UITextMdMedium>
      </CommunityButton>
      <CommunityButton
        v-if="user?.contactId != UserState.state.currentUser?.contactId"
        id="view-profile"
        class="mt-4 h-12 w-full uppercase"
        button-type="tertiary"
        size="small"
        @click="handleFollowUser"
        ><UITextMdMedium class="font-semibold">
          {{ isFollowing ? t('common.following') : t('common.follow') }}
        </UITextMdMedium>
      </CommunityButton>
      <UITooltip
        v-if="user?.contactId != UserState.state.currentUser?.contactId"
        id="user-profile-popover-chat-btn"
        :placement="'bottom'"
      >
        <div
          v-if="user?.isChatDisabled"
          class="rounded-lg bg-communities-background p-2"
        >
          <UITextSmNormal class="text-communities-font-primary">
            {{ t('userProfilePopover.chatDisabled') }}
          </UITextSmNormal>
        </div>
        <template #trigger>
          <CommunityButton
            id="popover-chat-btn"
            class="mt-4 h-12 w-full uppercase"
            button-type="primary"
            size="small"
            :disabled="!UserState.state.currentUser || user?.isChatDisabled"
            @click="openChatWindow(user)"
            ><UITextMdMedium class="font-semibold">{{
              t('common.chats')
            }}</UITextMdMedium>
          </CommunityButton>
        </template>
      </UITooltip>
    </div>
  </div>
</template>
<style scoped>
.n-progress.n-progress--circle {
  width: 170px;
}

.profile-popover-layout {
  grid-template-columns: auto 1fr;
  gap: 22px;
}

.grid-auto-col-2 {
  grid-template-columns: auto 1fr;
  gap: 6px;
}
</style>
<style>
.social-icons > path {
  fill: var(--fontSecondary);
}
.social-icons > path:hover {
  fill: var(--fontPrimary);
}
.social-icons > g > path:first-child {
  fill: var(--fontSecondary);
}
.social-icons > g > path:first-child:hover {
  fill: var(--fontPrimary);
}
.instagram-icon > g > path {
  fill: var(--fontSecondary);
}
.instagram-icon > g > path:hover {
  fill: var(--fontPrimary);
}
</style>
